import {createBrowserRouter, Navigate, RouterProvider} from 'react-router-dom';

import Login from '../pages/Login';
import Home from '../pages/Home';
import ProtectedRoute from './ProtectedRoute';
import Logout from 'src/pages/Logout';
import DeviceLayout from 'src/pages/Devices';
import Devices from 'src/pages/Devices/Devices';
import AccessGroup from 'src/pages/Devices/AccessGroup';
import AddAccessGroup from 'src/pages/Devices/AddAccessGroup';
import PropertyLayout from 'src/pages/Property';
import ApartmentDetail from 'src/pages/Property/ApartmentDetail';
import Apartment from 'src/pages/Property/Apartment';
import Tenant from 'src/pages/Tenant';
import {useEffect} from 'react';
import LocalDB from 'src/utils/localDB';
import {useAppDispatch, useAppSelector} from 'src/redux/hooks';
import {
  selectAccount,
  setJwtToken,
  setRole,
  setUserDetail,
  setUserId,
} from 'src/redux/slice/accountSlice';
import {store} from 'src/redux/store';
// import {userServiceDetail} from 'src/apis';
// import {AuthLoginResponse, Role, User} from 'src/apis/autoGenerate';
import {userServiceDetail} from 'src/apis/autoGenerate/services';
import {AuthLoginResponse, Role, User} from 'src/apis/autoGenerate/types';
import {getWeatherByLatLon} from 'src/apis/config/axiosConfigNoCredential';
import ManageAccessGroupTenant from 'src/pages/Devices/ManageAccessGroupTenant';
import DeliveryPage from 'src/pages/Delivery';
import AmenityLayout from 'src/pages/Amenity';
import AmenityList from 'src/pages/Amenity/AmenityList';
import ReservationsPage from 'src/pages/Amenity/Reservations';
import EventSearchLayout from 'src/pages/EventSearch';
import CallEvents from 'src/pages/EventSearch/CallEvent';
import DoorEvents from 'src/pages/EventSearch/DoorEvent';
import UserPage from 'src/pages/Settings/Users';
import WelcomePage from 'src/pages/Settings/WelcomePage';
import Profile from 'src/pages/Profile';
import SetupPassword from 'src/pages/SetupPassword';
import VerifyEmail from 'src/pages/VerifyEmail';
import AcceptTenantInvitation from 'src/pages/AcceptInvitation/AcceptTenantInvitation';
import LazyLoad from './lazyLoad';
import ManageAccessGroupOrgUsers from 'src/pages/Devices/ManageAccessGroupOrgUsers';
import IntegrationPage from 'src/pages/Settings/Integration';
import AddCommercialPropertyModal from 'src/components/Modals/AddCommercialProperty';
import CommercialDetail from 'src/pages/Property/CommercialDetail';
import LicensePage from 'src/pages/Settings/License';
import FrontDeskPage from 'src/pages/FrontDesk';
import {getHomePath, HOME_PATH_DASHBOARD} from 'src/utils/contextPreferences';

function DefaultHome() {
  const account = useAppSelector(selectAccount);
  const path = getHomePath(account.userId, account.role?.name);
  if (path !== HOME_PATH_DASHBOARD) {
    return <Navigate to={path} replace />;
  }
  return <Home />;
}

const GuestPassLazy = LazyLoad(() => import('../pages/GuestPass'));

export const routesForPublic = [
  {
    path: '/login',
    element: <Login />,
  },
  {
    path: '/service',
    element: <div>Service Page</div>,
  },
  {
    path: '/about-us',
    element: <div>About Us</div>,
  },
  {
    path: '/terms-of-service',
    element: <div>Terms of service and privacy</div>,
  },
  {
    path: '/guest-pass/:passCode',
    element: <GuestPassLazy />,
  },
  {
    path: '/setup-password',
    element: <SetupPassword />,
  },
  {
    path: '/verify-email',
    element: <VerifyEmail />,
  },
  {
    path: '/tenant/accept-invitation',
    element: <AcceptTenantInvitation />,
  },
];

const routesForAuthenticatedOnly = [
  {
    path: '/',
    element: <ProtectedRoute />,
    children: [
      {
        path: '/',
        element: <DefaultHome />,
      },
      {
        path: '/dashboard',
        element: <Home />,
      },
      {
        path: '/devices',
        element: <DeviceLayout />,
        children: [
          {path: '/devices', element: <Devices />},
          {path: '/devices/accessGroup', element: <AccessGroup />},
          {path: '/devices/accessGroup/add', element: <AddAccessGroup />},
          {path: '/devices/accessGroup/edit/:groupId', element: <AddAccessGroup />},
          {
            path: '/devices/accessGroup/manageTenants/:groupId',
            element: <ManageAccessGroupTenant />,
          },
          {
            path: '/devices/accessGroup/manageOrgUsers/:accessGroupId',
            element: <ManageAccessGroupOrgUsers />,
          },
        ],
      },
      {
        path: '/property',
        element: <PropertyLayout />,
        children: [
          {path: '/property', element: <Apartment />},
          {path: '/property/apartmentDetail/:apartmentId', element: <ApartmentDetail />},
          {path: '/property/commercialDetail/:id', element: <CommercialDetail />},
        ],
      },
      {
        path: '/tenant',
        children: [{path: '/tenant', element: <Tenant />}],
      },
      {
        path: '/visitors',
        element: <div>Visitors</div>,
      },
      {
        path: '/front-desk',
        element: <FrontDeskPage />,
      },
      {
        path: '/delivery',
        element: <DeliveryPage />,
      },
      {
        path: '/amenities',
        element: <AmenityLayout />,
        children: [
          {path: '/amenities', element: <AmenityList />},
          {path: '/amenities/reservations', element: <ReservationsPage />},
        ],
      },
      {
        path: '/eventSearch',
        element: <EventSearchLayout />,
        children: [
          {path: '/eventSearch', element: <CallEvents />},
          {path: '/eventSearch/callEvent', element: <CallEvents />},
          {path: '/eventSearch/doorEvent', element: <DoorEvents />},
        ],
      },
      {
        path: '/community',
        element: <div>Community</div>,
      },
      {
        path: '/settings',
        children: [
          {path: '/settings/users', element: <UserPage />},
          {path: '/settings/welcomePage', element: <WelcomePage />},
          {path: '/settings/integration', element: <IntegrationPage />},
          {
            path: '/settings/access-control',
            element: <Navigate to="/settings/integration?tab=pdk" replace />,
          },
          {path: '/settings/license', element: <LicensePage />},
        ],
      },
      {
        path: '/billing',
        element: <div>Billing</div>,
      },
      {
        path: '/profile',
        element: <Profile />,
      },
      {
        path: '/logout',
        element: <Logout />,
      },
    ],
  },
];

const Routes = () => {
  const dispatch = useAppDispatch();
  const account = useAppSelector(selectAccount);

  // useEffect(() => {
  //   const userId = LocalDB.get(LocalDB.alias.userId);
  //   const jwtToken = LocalDB.get(LocalDB.alias.token);
  //   const role = LocalDB.get(LocalDB.alias.role);
  //   console.log('🚀 ~ file: index.tsx:119 ~ useEffect ~ account:', account, userId);
  //   if (userId && !account.userId) {
  //     dispatch(setUserId(userId));
  //     userServiceDetail(userId).then(res => {
  //       const r = res as unknown as User;
  //       dispatch(setUserDetail(r));
  //     });
  //   }
  //   if (jwtToken && !account.jwtToken) {
  //     dispatch(setJwtToken(jwtToken));
  //   }
  //   if (role && !account.role) {
  //     dispatch(setRole(JSON.parse(role)));
  //   }
  // }, [account, dispatch]);

  const router = createBrowserRouter([...routesForPublic, ...routesForAuthenticatedOnly]);
  return <RouterProvider router={router} />;
};

export default Routes;
